import GD from '../GD'
import { LocalStorage, LS, Services, IndexDB, DCCAPI } from '../utils'
import { WSMessage } from '../enums'
import { Config } from './index'

class Messages {
  private ls: LocalStorage = new LocalStorage()
  private idb: IndexDB = new IndexDB()
  private messages: IMessage[] = []
  private lastUpdatedTime: number
  private lastUpdateTimerId: ReturnType<typeof setTimeout>
  private profile: IProfile
  private chatUID: string
  private isOffline = false
  private loadBeforeID = 0

  constructor() {
    GD.AUTH_COMPLETE.subscribe(data => {
      this.profile = data.profile
    })

    GD.WS_MSG.subscribe(async packet => {
      switch (packet.method) {
        case WSMessage.MESSAGE_ADD_OFFLINE:
        case WSMessage.MESSAGE_ADD_PREVIEW:
        case WSMessage.MESSAGE_ADD: {
          const message = packet.data as IMessageUnparsed
          const isOffline = packet.method === WSMessage.MESSAGE_ADD_OFFLINE

          void this.idb.addMessage(message)
          await this.addMsg(packet.data as IMessageUnparsed, isOffline)
          break
        }
        case WSMessage.MESSAGE_EDIT: {
          await this.idb.putMessage(packet.data as IMessageUnparsed)
          await this.editMsg(packet.data as IMessageUnparsed)
          break
        }
        case WSMessage.MESSAGE_REMOVE: {
          await this.idb.deleteMessage(packet.data as IMessageUnparsed)
          await this.deleteMsg(packet.data as IMessageUnparsed)
          break
        }
        case WSMessage.TOTAL_ONLINE: return
      }

      await this.renderMessages()
    })

    GD.SET_CHAT.subscribe(async chat => {
      this.chatUID = chat.uid
      this.startWSWatcher() // start watching for messages & connection

      this.lastUpdatedTime = +new Date()

      let messages = await this.fetchMessages(chat.uid)

      if (messages) {
        void this.idb.putMessages(messages, chat.uid)
      }
      else {
        messages = await this.idb.getMessages(chat.uid)
      }

      for (const item of messages) {
        await this.addMsg(item)
      }

      await this.renderMessages()
    })

    GD.FETCH_MORE_MESSAGES.listener = async () => {
      await this.fetchMessages(this.chatUID as string, -this.loadBeforeID, 50)
      await this.renderMessages()
    }

    GD.INVOKE_DELAYED_EVENTS.subscribe(async (packets: DelayedPacket[]) => {
      const chatUID =
        packets.find((packet: DelayedPacket) => packet.method === WSMessage.CHAT_USER_ENTER)
          ?.data?.chatUID || this.chatUID

      if (!chatUID) return

      GD.SET_MESSAGES.invoke([])
      await this.fetchMessages(chatUID)
      await this.renderMessages()
    })

    GD.WS_MSG_SENT_VIA_HTTP.subscribe(async data => {
      await this.updateChatMessagesViaPHP(data.chatUID)
    })

    GD.SERVICE_READY.invoke('Messages')
  }

  private async fetchMessages(chatUID: string, lastID = 0, limit = 100): Promise<IMessageUnparsed[] | null> {
    const hash = this.ls.get(LS.MESSAGES_HASH)

    const { error, errorMsg, data } = await GD.REQ_HTTP.request({
      method: 'chat.hGetMessages',
      chatUID: chatUID,
      limit: limit,
      banan: lastID,
      hash: (hash && !lastID) ? hash : -1,
    })

    if (error) {
      GD.ALERT_ERROR.invoke('Can\'t get messages, ' + errorMsg?.substr(10) + '...')
      return null
    }

    if (!data) {
      return null
    }

    if (data.hash) this.ls.set(LS.MESSAGES_HASH, data.hash)

    const messages: IMessageUnparsed[] = data.messages ?? data

    this.loadBeforeID = +messages[messages.length - 1]?.id || 0

    return messages
  }

  async prepareMessage(msg: IMessageUnparsed): Promise<IMessage> {
    const chat: ChatVO = await GD.GET_CHAT.request()

    let mine: boolean
    let side: 'left' | 'right'
    let type: MessageType = 'text'
    let sys: SystemIMessage | undefined
    let text: string = DCCAPI.unpack(msg.text, chat.securityKey)
    let user_name: string = msg.user_name
    let user_avatar: string = msg.user_avatar

    if (text.startsWith(Config.systemSequence)) {
      type = 'system'

      let sysData: SystemIMessage | null = null

      try {
        sysData = JSON.parse(text.substr(Config.systemSequence.length))
      }
      catch (e) {
        GD.LOGE.invoke(['Can\'t parse system message: ' + text, e])
      }

      if (sysData) {
        let additionalData: AdditionalData = sysData.additionalData

        if (sysData.type === 'file') {
          type = 'file'
        }

        if (sysData.type === 'file' && sysData.fileType === 'cimg') {
          type = 'img'
        }

        if (sysData.type === 'file' && sysData.fileType === 'img') {
          type = 'oldimg'
        }

        if ('method' in sysData) {
          if (sysData.method.toLowerCase() === 'vicomplete') {
            type = 'vi'
          }

          if (sysData.method.toLowerCase() === 'vistart') {
            type = 'vi'
          }

          if (sysData.method.toLowerCase() === 'vi_later') {
            type = 'vi'
          }

          if (sysData.method.toLowerCase() === 'dukasnotesrequest') {
            type = 'dukanotes'
          }

          if (sysData.method.toLowerCase() === 'vifail') {
            type = 'vi'
          }

          if (sysData.method === 'credentials' && sysData.type === 'credentials') {
            type = 'credentials'
          }

          if (sysData.method.toLowerCase() === 'voicesent' && sysData.type === 'voice') {
            type = 'voice'
          }

          if (sysData.type === 'chatSystem') {
            type = 'chatSystem'
          }

          if (sysData.type === 'chatSystem' && sysData.method === 'botMenu') {
            type = 'botMenu'
            additionalData = JSON.parse(sysData.additionalData as string)
          }

          if (sysData.type === 'chatSystem' && sysData.method === 'botCommand') {
            type = 'botCommand'
          }
        }

        let newText = text.substr(Config.systemSequence.length)

        if (sysData.title != null && sysData.title.length > 0 && newText.length < 1) {
          newText = sysData.title
        }
        else {
          if (!newText || newText.length < 1) newText = 'sys msg without title'
        }

        text = newText
        sys = { ...sysData, additionalData }
      }
    }

    if (chat.ownerID === 'guest' && msg.user_name.startsWith('guest')) {
      mine = true
      side = 'right'
    }
    else if (chat.ownerID !== msg.user_uid) {
      side = 'left'
      mine = false
    }
    else {
      side = 'right'
      mine = true
    }

    if (this.profile?.type !== 'guest') {
      if (this.profile?.uid === msg.user_uid) {
        side = 'right'
        mine = true
      }
      else {
        side = 'left'
        mine = false
      }
    }

    if (!mine && chat.type === 'company') {
      user_name = 'Dukascopy'
      user_avatar = 'dukascopy'
    }

    return {
      id: Number(msg.id),
      type: type,
      chatUID: (msg as IMessageUnparsed).chat_uid,
      created: msg.created,
      date: msg.created,
      num: Number(msg.num),
      mine: mine,
      side: side,
      text: text,
      version: Number(msg.version),
      user_uid: msg.user_uid,
      user_name: user_name,
      user_avatar: user_avatar,
      status: msg.status,
      parsed: true,
      sys: sys,
    }
  }

  private async addMsg(message: IMessageUnparsed, offline = false): Promise<void> {
    const msg: IMessage = await this.prepareMessage(message)

    if (msg.sys) {
      if (msg.sys.type === 'chatSystem' && !msg.sys.additionalData && !msg.sys.title) {
        return
      }
      else {
        const addData = msg.sys.additionalData as BotAdditionalData | undefined
        // Не отображаем опросники
        if (!addData?.menu?.items && addData?.rateBot) {
          return
        }
      }
    }

    this.messages = [...this.messages, msg]

    if (this.messages.length > 1000) this.messages.shift()

    if (offline) this.isOffline = true

    if (!offline && this.isOffline) {
      this.messages = this.messages.filter(m => m.status !== 'error')
      this.isOffline = false
    }
  }

  private async editMsg(data: IMessageUnparsed): Promise<void> {
    const message: IMessage = await this.prepareMessage(data)
    this.messages = this.messages.map(m => m.id === message.id ? { ...m, status: message.status, text: message.text } : m)
  }

  private async deleteMsg(data: IMessageUnparsed): Promise<void> {
    const message: IMessage = await this.prepareMessage(data)
    this.messages = this.messages.map(m => m.id === message.id ? { ...m, status: data.status, text: '' } : m)
  }

  private async renderMessages() {
    let arr = this.messages

    if (!arr || arr.length === 0) {
      return []
    }

    arr.sort((a, b) => {
      if (a.num > b.num)
        return 1
      if (b.num > a.num)
        return -1
      return 0
    })

    const l = arr.length
    let imageMessageGroup: IMessage[] = []

    for (let i = 0; i < l; i++) {
      const msg: IMessage = arr[i]
      const nextMessage: IMessage | undefined = arr[i + 1]
      let prev: IMessage | null = null

      msg.position = 'standalone'
      msg.date = undefined

      if (i > 0) {
        prev = arr[i - 1]
      }

      if (msg.type === 'img') {
        if (nextMessage && nextMessage.type === 'img') {
          const isMoreThan5MinPassed = (nextMessage.created - msg.created) > 60 * 5
          const isAnotherUserPhoto = msg.mine !== nextMessage.mine
          const isLastGroupImage = imageMessageGroup.length === 9

          if (isMoreThan5MinPassed || isAnotherUserPhoto || isLastGroupImage) {
            imageMessageGroup.push(msg)
            msg.imageGroup = imageMessageGroup
            imageMessageGroup = []
          }
          else {
            imageMessageGroup.push(msg)
            msg.imageGroup = undefined
            continue
          }

        }
        else {
          imageMessageGroup.push(msg)
          msg.imageGroup = imageMessageGroup
          imageMessageGroup = []
        }
        continue
      }

      if (!prev) {
        msg.date = Services.dateFormatter.format(msg.created, '%d, %M, %y')
        continue
      }

      const curDayOfYear = Services.dateFormatter.getDayOfYear(msg.created)
      const prevDayOfYear = Services.dateFormatter.getDayOfYear(prev.created)

      const timeOffset = msg.created - prev.created

      if (msg.user_uid === prev.user_uid && msg.type === prev.type && curDayOfYear === prevDayOfYear && timeOffset < 60 * 5) {
        msg.position = 'middle'
        if (prev.position === 'standalone')
          prev.position = 'first'
      }
      else {
        if (prev.position !== 'standalone')
          prev.position = 'last'
      }
      if (curDayOfYear !== prevDayOfYear) {
        msg.date = Services.dateFormatter.format(msg.created, '%d, %M, %y')
      }
    }

    arr = arr.filter(el => !(el.type === 'img' && !el.imageGroup))

    const last: IMessage | undefined = arr[arr.length - 1]

    if (last?.position === 'middle') {
      last.position = 'last'
    }

    GD.SET_MESSAGES.invoke(arr)
  }

  // TODO: Check all 3 bellow methods for proper work
  private startWSWatcher() {
    setTimeout(async () => {
      const state = await GD.REQ_WS_STATUS.request()
      if (state > 1) {
        // PASS LAST RECEIVED MSG ID
        if (this.chatUID)
          await this.updateChatMessagesViaPHP(this.chatUID)
      }
      this.startWSWatcher()
    }, 30 * 1000)
  }

  private async updateChatMessagesViaPHP(chatUID: string): Promise<void> {
    let lastUpdated = this.lastUpdatedTime

    if (!lastUpdated) lastUpdated = 0
    if (+new Date() - lastUpdated < 2000) {
      if (this.lastUpdateTimerId)
        clearTimeout(this.lastUpdateTimerId)
      this.lastUpdateTimerId = setTimeout(() => {
        this.updateChatMessagesViaPHP(chatUID)
      }, 3000)
      return
    }

    await this.fetchMessages(chatUID, 0, 15)
    await this.renderMessages()
  }
}

export default Messages